import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom"
import AuthProvider from "@/v2-backoffice-client/auth/AuthProvider"
import AuthGuard from "@/v2-backoffice-client/auth/AuthGuard"
import AppViewSheetBreadcrumbProvider from "@/v2-backoffice-client/app/AppViewSheetBreadcrumbProvider"
import AppSidebar from "@/v2-backoffice-client/app/AppSidebar"
import NotificationCenterProvider from "@/v2-backoffice-client/notification/NotificationCenterProvider"
import NotificationCenterDock from "@/v2-backoffice-client/notification/NotificationCenterDock"

// Pages
import AuthLoginPage from "@/v2-backoffice-client/auth/AuthLoginPage"
import AuthLogoutPage from "@/v2-backoffice-client/auth/AuthLogoutPage"
import CustomerPage from "@/v2-backoffice-client/customer/CustomerPage"
import { LICENSE_STATUS_TRIAL } from "@/v2-backoffice-client/license/license.constants"
import LicensingPage from "@/v2-backoffice-client/license/LicensingPage"
import TenantPage from "@/v2-backoffice-client/tenant/TenantPage"
import AgentConversationPage from "@/v2-backoffice-client/agentConversation/AgentConversationPage"
import UserPage from "@/v2-backoffice-client/user/UserPage"
import InvitationPage from "@/v2-backoffice-client/invitation/InvitationPage"
import FeatureFlagsPage from "@/v2-backoffice-client/featureFlags/FeatureFlagsPage"
import AnalyticsPage from "@/v2-backoffice-client/analytics/AnalyticsPage"
import LayerPage from "@/v2-backoffice-client/layer/LayerPage"
import SignInPage from "@/v2-backoffice-client/signIn/SignInPage"
import SettingPage from "@/v2-backoffice-client/settings/SettingPage"
import UptimePage from "@/v2-backoffice-client/uptime/UptimePage"
import LegalBillingPage from "@/v2-backoffice-client/legalBilling/LegalBillingPage"
import TranslationPage from "@/v2-backoffice-client/translation/TranslationPage"
import TranslationRedirect from "@/v2-backoffice-client/translation/TranslationRedirect"
import { IS_TRANSLATION_ENABLED } from "@/v2-backoffice-client/translation/translation.constants"

// View Sheets
import AppViewSheet from "@/v2-backoffice-client/app/AppViewSheet"
import CustomerViewSheet from "@/v2-backoffice-client/customer/view/CustomerViewSheet"
import TenantViewSheet from "@/v2-backoffice-client/tenant/view/TenantViewSheet"
import AgentConversationViewSheet from "@/v2-backoffice-client/agentConversation/view/AgentConversationViewSheet"
import UserViewSheet from "@/v2-backoffice-client/user/view/UserViewSheet"
import InvitationViewSheet from "@/v2-backoffice-client/invitation/view/InvitationViewSheet"
import LayerViewSheet from "@/v2-backoffice-client/layer/view/LayerViewSheet"
import LayerCreateSheet from "@/v2-backoffice-client/layer/create/LayerCreateSheet"
import LayerCategoryViewSheet from "@/v2-backoffice-client/layer/category/view/LayerCategoryViewSheet"
import BasemapViewSheet from "@/v2-backoffice-client/basemap/view/BasemapViewSheet"
import BasemapCreateSheet from "@/v2-backoffice-client/basemap/create/BasemapCreateSheet"
import NavLayerViewSheet from "@/v2-backoffice-client/navlayer/view/NavLayerViewSheet"
import NavLayerCreateSheet from "@/v2-backoffice-client/navlayer/create/NavLayerCreateSheet"
import UptimeViewSheet from "@/v2-backoffice-client/uptime/view/UptimeViewSheet"
import LicensePlanViewSheet from "@/v2-backoffice-client/license/plan/view/LicensePlanViewSheet"
import LicenseAccountViewSheet from "@/v2-backoffice-client/license/account/view/LicenseAccountViewSheet"
import LicenseFeatureGroupViewSheet from "@/v2-backoffice-client/license/featureGroup/view/LicenseFeatureGroupViewSheet"
import LicenseAddOnViewSheet from "@/v2-backoffice-client/license/addOns/catalog/view/LicenseAddOnViewSheet"
import LicenseCapabilityViewSheet from "@/v2-backoffice-client/license/changes/LicenseCapabilityViewSheet"
import LicenseContentViewSheet from "@/v2-backoffice-client/license/changes/LicenseContentViewSheet"

export const APP_VIEW_SHEET_ROUTES = {
  customer: CustomerViewSheet,
  tenant: TenantViewSheet,
  agentConversation: AgentConversationViewSheet,
  user: UserViewSheet,
  invitation: InvitationViewSheet,
  plan: LicensePlanViewSheet,
  billingAccount: LicenseAccountViewSheet,
  featureGroup: LicenseFeatureGroupViewSheet,
  addOn: LicenseAddOnViewSheet,
  // The Feature access panels; their hrefs are built by the helpers in
  // license.changes.constants, which `satisfies` these keys.
  capability: LicenseCapabilityViewSheet,
  mapContent: LicenseContentViewSheet,
  layer: LayerViewSheet,
  layerCreate: LayerCreateSheet,
  layerCategory: LayerCategoryViewSheet,
  basemap: BasemapViewSheet,
  basemapCreate: BasemapCreateSheet,
  navLayer: NavLayerViewSheet,
  navLayerCreate: NavLayerCreateSheet,
  uptime: UptimeViewSheet,
}

export type AppViewSheetKind =
  | keyof typeof APP_VIEW_SHEET_ROUTES
  | undefined
  | null

function AppRouter() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppViewSheetBreadcrumbProvider>
          <Routes>
            <Route path="/login" element={<AuthLoginPage />} />
            <Route path="/logout" element={<AuthLogoutPage />} />
            <Route
              path="/"
              element={
                <AuthGuard>
                  <NotificationCenterProvider>
                    <AppSidebar />
                    {/* Docked left of the page, right of the nav — the
                        notification center outlives route changes. */}
                    <NotificationCenterDock />
                    <Outlet />
                    <AppViewSheet />
                  </NotificationCenterProvider>
                </AuthGuard>
              }
            >
              <Route index element={<Navigate to="/customers" replace />} />
              <Route path="customers" element={<CustomerPage />} />
              {/* The old standalone licenses board — absorbed into the
                  customers board's status pills (?status=<code>); the Trial
                  pill was its default view. */}
              <Route
                path="licenses"
                element={
                  <Navigate
                    to={`/customers?status=${LICENSE_STATUS_TRIAL}`}
                    replace
                  />
                }
              />
              <Route path="licensing" element={<LicensingPage />} />
              <Route path="tenants" element={<TenantPage />} />
              <Route
                path="agent-conversations"
                element={<AgentConversationPage />}
              />
              <Route path="users" element={<UserPage />} />
              <Route path="invitations" element={<InvitationPage />} />
              <Route path="layers" element={<LayerPage />} />
              <Route path="activity" element={<SignInPage />} />
              {/* Old bookmarks/links from when the page was "Sign-ins". */}
              <Route
                path="sign-ins"
                element={<Navigate to="/activity" replace />}
              />
              <Route path="feature-flags" element={<FeatureFlagsPage />} />
              <Route path="uptime" element={<UptimePage />} />
              <Route path="legal-billing" element={<LegalBillingPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              {/* Dev-environments-only: the feature authoring DB
                  (v2-features-db) exists solely on the dev SQL server, so
                  ENV_TYPE=prod deployments hide the page instead of
                  shipping a permanently-erroring one. A direct URL falls
                  through to the catch-all redirect below. */}
              {IS_TRANSLATION_ENABLED ? (
                <>
                  <Route path="features" element={<TranslationPage />} />
                  <Route path="features/:view" element={<TranslationPage />} />
                  {/* The page was "Translation" for as long as text was the
                      only facet its rows edited. Old bookmarks and the
                      view-deep links in Slack/Linear land on the same view. */}
                  <Route
                    path="translation"
                    element={<Navigate to="/features" replace />}
                  />
                  <Route
                    path="translation/:view"
                    element={<TranslationRedirect />}
                  />
                </>
              ) : null}
              {/* The Layer Studio dissolved into the Layers page; old
                  bookmarks land there. */}
              <Route
                path="layer-studio"
                element={<Navigate to="/layers" replace />}
              />
              <Route path="settings" element={<SettingPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </AppViewSheetBreadcrumbProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}

export default AppRouter
